import { TaskStatus } from '@varos/research-sdk';
import { ReportingControllerBaseConfig } from '../../../base';
import { TaskItemController } from './taskItemInterface';
import { TaskItemStatusProps, TaskItemViewProps } from './taskItemProps';
import { useMemo } from 'react';
import { BsCircleHalf } from 'react-icons/bs';
import { RiProgress4Line, RiProgress8Fill } from 'react-icons/ri';

export function createTaskItemController(
    config: ReportingControllerBaseConfig
): TaskItemController {
    const {
        kernel: {
            infra: { formatter },
        },
    } = config;
    function getStatus(status: TaskStatus): TaskItemStatusProps {
        const mapping: Record<TaskStatus, TaskItemStatusProps> = {
            pending: {
                kind: 'pending',
                label: 'Pending',
                colorScheme: 'gray',
                Icon: RiProgress4Line,
            },
            in_progress: {
                kind: 'in-progress',
                label: 'In Progress',
                colorScheme: 'orange',
                Icon: RiProgress4Line,
            },
            completed: {
                kind: 'completed',
                label: 'Completed',
                colorScheme: 'green',
                Icon: RiProgress8Fill,
            },
            failed: {
                kind: 'failed',
                label: 'Failed',
                colorScheme: 'red',
                Icon: RiProgress4Line,
            },
            canceled: {
                kind: 'canceled',
                label: 'Canceled',
                colorScheme: 'gray',
                Icon: RiProgress8Fill,
            },
        };
        return mapping[status];
    }

    const TRUNCATION_LIMIT = 2;

    return {
        useProps(context, item, props) {
            const relative = useMemo(
                () => formatter.timeago(item.task.created_at),
                [item.task.created_at]
            );
            const [currentStatus] = item.statuses;
            const truncation: TaskItemViewProps['truncation'] =
                item.statuses.length > 1
                    ? {
                          label: props.disclosure.isOpen ? 'View less' : 'View all',
                          isOpen: props.disclosure.isOpen,
                          button: {
                              onClick: props.disclosure.onToggle,
                          },
                      }
                    : null;

            return {
                id: item.task.id,
                title: item.task.display_name,
                description: item.task.description,
                status: currentStatus
                    ? getStatus(currentStatus.kind)
                    : getStatus(item.task.status),
                created: {
                    relative,
                    absolute: formatter.date(item.task.created_at),
                },
                statuses: (truncation?.isOpen
                    ? item.statuses
                    : item.statuses.slice(0, TRUNCATION_LIMIT)
                ).map((status) => ({
                    id: status.id,
                    title: status.display_name,
                    description: status.description,
                    status: getStatus(status.kind),
                    created: {
                        relative: formatter.timeago(status.created_at),
                        absolute: formatter.date(status.created_at),
                    },
                })),
                truncation,
            };
        },
    };
}
